import React, { useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PresetInfo } from './PresetCard';
import PresetCard from './PresetCard';
import ProgressiveSessions from './ProgressiveSessions';
import GuidedSessions, { GuidedSessionInfo, GUIDED_SESSIONS } from './GuidedSessions';
import AmbienceMixer, { AmbienceSound } from './AmbienceMixer';
import AdvancedSession, { SessionStep } from './AdvancedSession';
import { Clock, CloudRain, Waves, Trees, CloudFog, CloudLightning, Droplets, Wind, Music, CloudSun, Sliders, TrendingUp, Sparkles, Headphones, Lock } from 'lucide-react';
import { ambientSoundPresets, SoundCategories } from '@/config/audioConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import CustomSession, { AdvancedSettings } from './CustomSession';
import { PRESET_SECTIONS } from '@/config/presetConfig';
import PresetSection from './PresetSection';
import { PresetFilter } from './PresetFilter';
import { Button } from "@/components/ui/button";
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface TabViewProps {
  presets: PresetInfo[];
  activePreset: PresetInfo | null;
  isPlaying?: boolean;
  onSelectPreset: (preset: PresetInfo) => void;
  onPlayPause: () => void;
  onCreateCustomSession: (frequency: number, duration: number, title: string, advancedSettings?: AdvancedSettings) => void;
  onCreateProgressiveSession: (
    startFreq: number, 
    endFreq: number, 
    duration: number, 
    transitionTime: number
  ) => void;
  onSelectGuidedSession: (session: GuidedSessionInfo) => void;
  onCreateAdvancedSession?: (steps: SessionStep[], ambientSounds?: AmbienceSound[]) => void;
  ambientSounds: AmbienceSound[];
  onAmbientVolumeChange: (id: string, volume: number) => void;
  playerRef?: React.RefObject<HTMLDivElement>;
  isPlayMode?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
}

const TabView: React.FC<TabViewProps> = ({
  presets,
  activePreset,
  isPlaying = false,
  onSelectPreset,
  onPlayPause,
  onCreateCustomSession,
  onCreateProgressiveSession,
  onSelectGuidedSession,
  onCreateAdvancedSession,
  ambientSounds,
  onAmbientVolumeChange,
  playerRef,
  isPlayMode = false,
  value,
  onValueChange
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const customSessionRef = useRef<HTMLDivElement>(null);
  const progressiveSessionRef = useRef<HTMLDivElement>(null);
  const [activeFilters, setActiveFilters] = React.useState<{
    frequencies: string[];
    soundTypes: string[];
    durations: string[];
  }>({
    frequencies: [],
    soundTypes: [],
    durations: []
  });

  const handleFilterChange = (filters: {
    frequencies: string[];
    soundTypes: string[];
    durations: string[];
  }) => {
    setActiveFilters(filters);
  };

  const currentSoundsMap = new Map(ambientSounds.map(sound => [sound.id, sound]));
  const configSounds = ambientSoundPresets;

  function getIconForSound(name: string, category?: string) {
    const nameLower = name.toLowerCase();
    
    if (nameLower.includes('rain')) return CloudRain;
    if (nameLower.includes('ocean') || nameLower.includes('waves')) return Waves;
    if (nameLower.includes('forest')) return Trees;
    if (nameLower.includes('thunder')) return CloudLightning;
    if (nameLower.includes('river')) return Droplets;
    if (nameLower.includes('wind')) return Wind;
    if (nameLower.includes('meditation')) return CloudSun;
    if (category === SoundCategories.INSTRUMENTS) return Music;
    if (category === SoundCategories.SOUNDSCAPES) return CloudFog;
    
    return CloudFog;
  }

  const enrichedAmbientSounds = ambientSounds.map(sound => {
    const configSound = configSounds.find(config => config.id === sound.id);
    return {
      ...sound,
      icon: configSound?.icon || getIconForSound(sound.name, configSound?.category),
      category: configSound?.category
    };
  });

  const handleCustomSessionCreate = (frequency: number, duration: number, title: string, advancedSettings?: AdvancedSettings) => {
    onCreateCustomSession(frequency, duration, title, advancedSettings);
  };

  const handleProgressiveSessionCreate = (
    startFreq: number,
    endFreq: number,
    duration: number,
    transitionTime: number
  ) => {
    onCreateProgressiveSession(startFreq, endFreq, duration, transitionTime);
  };

  const handleAdvancedSessionCreate = (steps: SessionStep[], activeSounds?: AmbienceSound[]) => {
    onCreateAdvancedSession?.(steps, activeSounds);
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePresetSelect = (preset: PresetInfo) => {
    onSelectPreset(preset);
    
    setTimeout(() => {
      if (playerRef && playerRef.current) {
        playerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  const handleProtectedFeatureClick = () => {
    navigate('/auth/signin');
  };

  const renderProtectedFeaturePrompt = () => (
    <Card className="bg-card/50 border-primary/10">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Sign in to Access
        </CardTitle>
        <CardDescription>
          Create an account to unlock custom sessions, guided meditations, and AI-powered recommendations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium">Premium Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Custom session designer</li>
            <li>Guided meditation sessions</li>
            <li>AI-powered recommendations</li>
            <li>Save your favorite sessions</li>
            <li>Track your session history</li>
          </ul>
        </div>
        <Button onClick={handleProtectedFeatureClick} className="w-full">
          Sign in to Continue
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className={`transition-all duration-300 ${isPlaying ? 'opacity-80' : 'opacity-100'} ${isPlayMode ? 'opacity-0 pointer-events-none' : ''}`}>
      <Tabs value={value} onValueChange={onValueChange}>
        <TabsList className="grid grid-cols-3 mb-6 w-full max-w-2xl mx-auto">
          <TabsTrigger value="presets" className="text-sm md:text-base">Presets</TabsTrigger>
          <TabsTrigger value="custom" className="text-sm md:text-base">Custom</TabsTrigger>
          <TabsTrigger value="guided" className="text-sm md:text-base">Guided</TabsTrigger>
        </TabsList>
        
        <TabsContent value="presets" className="space-y-8 animate-fade-in">
          {PRESET_SECTIONS.map((section, index) => {
            const filteredPresets = section.presets.filter(preset => {
              // If no filters are active, show all presets
              if (!activeFilters.frequencies.length && 
                  !activeFilters.soundTypes.length && 
                  !activeFilters.durations.length) {
                return true;
              }

              // Check frequency type
              if (activeFilters.frequencies.length) {
                const presetFrequency = preset.frequency;
                const matchesFrequency = activeFilters.frequencies.some(filter => {
                  switch (filter) {
                    case 'delta':
                      return presetFrequency >= 0.5 && presetFrequency < 4;
                    case 'theta':
                      return presetFrequency >= 4 && presetFrequency < 8;
                    case 'alpha':
                      return presetFrequency >= 8 && presetFrequency < 12;
                    case 'beta':
                      return presetFrequency >= 12 && presetFrequency < 30;
                    case 'gamma':
                      return presetFrequency >= 30 && presetFrequency <= 50;
                    default:
                      return false;
                  }
                });
                if (!matchesFrequency) return false;
              }

              // Check sound type
              if (activeFilters.soundTypes.length) {
                const hasAmbientSounds = preset.ambientSounds && preset.ambientSounds.length > 0;
                const isSoundscape = hasAmbientSounds && preset.ambientSounds?.some(sound => 
                  sound.id === 'space' || sound.id === 'atmospheric' || sound.id === 'tribute'
                );
                
                const matchesSoundType = activeFilters.soundTypes.some(filter => {
                  switch (filter) {
                    case 'pure':
                      return !hasAmbientSounds;
                    case 'ambient':
                      return hasAmbientSounds && !isSoundscape;
                    case 'soundscape':
                      return isSoundscape;
                    default:
                      return false;
                  }
                });
                if (!matchesSoundType) return false;
              }

              // Check duration
              if (activeFilters.durations.length) {
                const duration = preset.duration;
                const matchesDuration = activeFilters.durations.some(filter => {
                  switch (filter) {
                    case 'short':
                      return duration >= 5 && duration < 15;
                    case 'medium':
                      return duration >= 15 && duration < 30;
                    case 'long':
                      return duration >= 30;
                    default:
                      return false;
                  }
                });
                if (!matchesDuration) return false;
              }

              return true;
            });

            // Only show sections that have matching presets
            if (filteredPresets.length === 0) return null;

            return (
              <div key={section.category} className={`animate-fade-in animate-delay-${index} preset-section`}>
                <PresetSection
                  section={{
                    ...section,
                    presets: filteredPresets
                  }}
                  activePresetId={activePreset?.id || null}
                  onPresetSelect={handlePresetSelect}
                  onFilterChange={handleFilterChange}
                />
              </div>
            );
          })}
        </TabsContent>
        
        <TabsContent value="custom" className="space-y-6 animate-fade-in">
          {user ? (
            <>
              <Card className="bg-card/50 border-primary/10">
                <CardHeader className="pb-2">
                  <CardTitle>Session Designer</CardTitle>
                  <CardDescription>
                    Design your own binaural beat experience with our custom tools
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div 
                      className="flex items-start gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors custom-session"
                      onClick={() => scrollToSection(customSessionRef)}
                      role="button"
                      tabIndex={0}
                      aria-label="Go to Session Designer"
                    >
                      <Sliders className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Session Designer</p>
                        <p>Create a binaural beat session with both basic and advanced frequency controls</p>
                      </div>
                    </div>
                    <div 
                      className="flex items-start gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                      onClick={() => scrollToSection(progressiveSessionRef)}
                      role="button"
                      tabIndex={0}
                      aria-label="Go to Progressive Session"
                    >
                      <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Progressive Session</p>
                        <p>Gradually transition from one brainwave state to another over your session duration</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div ref={customSessionRef} className="frequency-slider">
                    <CustomSession
                      onCreateSession={handleCustomSessionCreate}
                      onCreateProgressiveSession={handleProgressiveSessionCreate}
                      onCreateAdvancedSession={handleAdvancedSessionCreate}
                    />
                  </div>
                  
                  <div className="md:hidden ambience-mixer">
                    <AmbienceMixer
                      sounds={enrichedAmbientSounds}
                      onVolumeChange={onAmbientVolumeChange}
                      isPlaying={isPlaying}
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <Sparkles className="h-8 w-8 text-primary mx-auto" />
                        <p className="text-lg font-medium">Coming Soon</p>
                        <p className="text-sm text-muted-foreground max-w-[200px]">
                          Progressive sessions are currently in development
                        </p>
                      </div>
                    </div>
                    <div className="opacity-50">
                      <ProgressiveSessions onCreateSession={handleProgressiveSessionCreate} />
                    </div>
                  </div>
                  
                  {onCreateAdvancedSession && (
                    <div className="relative">
                      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                        <div className="text-center space-y-2">
                          <Sparkles className="h-8 w-8 text-primary mx-auto" />
                          <p className="text-lg font-medium">Coming Soon</p>
                          <p className="text-sm text-muted-foreground max-w-[200px]">
                            Multi-Stage Session Designer is currently in development
                          </p>
                        </div>
                      </div>
                      <AdvancedSession onCreateSession={handleAdvancedSessionCreate} />
                    </div>
                  )}
                </div>
                
                <div className="hidden md:block ambience-mixer">
                  <AmbienceMixer
                    sounds={enrichedAmbientSounds}
                    onVolumeChange={onAmbientVolumeChange}
                    isPlaying={isPlaying}
                  />
                </div>
              </div>
            </>
          ) : (
            renderProtectedFeaturePrompt()
          )}
        </TabsContent>
        
        <TabsContent value="guided" className="space-y-6 animate-fade-in">
          {user ? (
            <div className="relative">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="text-center space-y-2">
                  <Sparkles className="h-8 w-8 text-primary mx-auto" />
                  <p className="text-lg font-medium">Coming Soon</p>
                  <p className="text-sm text-muted-foreground max-w-[200px]">
                    Guided meditation sessions are currently in development
                  </p>
                </div>
              </div>
              <div className="opacity-50">
                <GuidedSessions
                  onSelectSession={onSelectGuidedSession}
                />
              </div>
            </div>
          ) : (
            renderProtectedFeaturePrompt()
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TabView;
